import { Helmet } from 'react-helmet-async';
import dayjs from 'dayjs';
import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
// @mui
import {
  Card,
  Table,
  Stack,
  Paper,
  Button,
  Popover,
  TableRow,
  MenuItem,
  TableBody,
  TableCell,
  Container,
  Typography,
  IconButton,
  TableContainer,
  CircularProgress,
  Pagination,
  Box,
  useMediaQuery,
  useTheme,
  Grid,
  Chip
} from '@mui/material';

import CustomModal from '../components/modal/CustomModal';
import FormCreateActivities from '../components/forms/activitiesForms/createActivity'
import Label from '../components/label';
import Iconify from '../components/iconify';
import { UserListHead, UserListToolbar } from '../sections/@dashboard/user';
import { useAxios } from '../context/AxiosHookContext';

const TABLE_HEAD = [
  { id: 'actividad', label: 'Actividad', alignRight: false },
  { id: 'anfitrion', label: 'Anfitrión', alignRight: false },
  { id: 'lastEnrollDate', label: 'Fecha límite', alignRight: false },
  { id: 'description', label: 'Descripción', alignRight: false },
  { id: 'limitSpaces', label: 'Cupos limitados', alignRight: false },
  { id: 'participantsNumber', label: 'Cant. Participantes', alignRight: false },
  { id: '' },
];

export default function UserPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [openModalActivities, setOpenModalActivities] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1)
  const [loading, setLoading] = useState(false)
  const [activityList, setActivitiesList] = useState([])
  const [valuesToEdit, setValuesToEdit] = useState({})
  const navigate = useNavigate();
  const [open, setOpen] = useState(null);
  const [filterName, setFilterName] = useState('');

  const ACTIVITY_API = `/activities/activities?&page=${page}&size=${10}&actividad=${filterName}`
  const axios = useAxios();

  const handleOpenMenu = (event, values) => {
    setOpen(event.currentTarget);
    setValuesToEdit(values)
  };

  const handleCloseMenu = () => {
    setOpen(null);
  };

  const getActivities = useCallback(async () => {
    try {
      setActivitiesList([])
      setLoading(true);
      const { data } = await axios.get(ACTIVITY_API);
      setActivitiesList(data.items);
      setTotalPages(data.total_pages)
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  }, [axios, ACTIVITY_API, setActivitiesList, setLoading]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleFilterByName = (event) => {
    setPage(1);
    setFilterName(event.target.value);
  };

  useEffect(() => {
    getActivities();
  }, [getActivities, page, filterName])

  // Mobile Card Component
  const ActivityCard = ({ activity }) => (
    <Card sx={{ mb: 2, p: 2 }}>
      <Stack spacing={2}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
            {activity.actividad}
          </Typography>
          <IconButton size="small" onClick={(e) => handleOpenMenu(e, activity)}>
            <Iconify icon={'eva:more-vertical-fill'} />
          </IconButton>
        </Stack>
        
        <Stack spacing={1}>
          <Box>
            <Typography variant="caption" color="text.secondary">Anfitrión:</Typography>
            <Typography variant="body2">{activity.anfitrion || 'N/A'}</Typography>
          </Box>
          
          <Box>
            <Typography variant="caption" color="text.secondary">Fecha límite:</Typography>
            <Label color={activity.is_active === false ? 'error' : 'success'} sx={{ ml: 1 }}>
              {dayjs(activity.lastEnrollDate).format('DD/MM/YYYY')}
            </Label>
          </Box>
          
          <Box>
            <Typography variant="caption" color="text.secondary">Descripción:</Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              {activity.description || 'N/A'}
            </Typography>
          </Box>
          
          <Stack direction="row" spacing={2}>
            <Box>
              <Typography variant="caption" color="text.secondary">Cupos limitados:</Typography>
              <Typography variant="body2">{activity.limitSpaces ? 'Sí' : 'No'}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Participantes:</Typography>
              <Typography variant="body2">
                {activity.limitSpaces ? activity.participantsNumber : 'Sin límites'}
              </Typography>
            </Box>
          </Stack>
        </Stack>
      </Stack>
    </Card>
  );

  return (
    <>
      <Helmet>
        <title>Actividades | ASISCORE</title>
      </Helmet>

        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
          <Typography variant="h4" gutterBottom>
            Actividades
          </Typography>
          <Button 
            onClick={() => { setOpenModalActivities(true);setValuesToEdit({}) }} 
            variant="contained" 
            startIcon={<Iconify icon="eva:plus-fill" />}
            size={isMobile ? "small" : "medium"}
          >
            {isMobile ? 'Nueva' : 'Nueva Actividad'}
          </Button>
        </Stack>

        <Card>
          <UserListToolbar filterName={filterName} onFilterName={handleFilterByName} />

          <Box sx={{ p: { xs: 1, sm: 1.5 } }}>
            {loading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={30} />
              </Box>
            )}
            
            <Stack spacing={1}>
              {activityList.length > 0 && activityList.map((activity) => (
                <Card key={activity._id} sx={{ 
                  p: 1.5, 
                  borderRadius: 2, 
                  boxShadow: 1,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    boxShadow: 3,
                    transform: 'translateY(-1px)'
                  }
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {/* Icon */}
                    <Box sx={{
                      width: 32,
                      height: 32,
                      borderRadius: 2,
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      <Iconify icon="eva:calendar-fill" sx={{ color: 'white', fontSize: 16 }} />
                    </Box>
                    
                    {/* Title & Host */}
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.9rem', lineHeight: 1.2 }}>
                        {activity.actividad}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                        {activity.anfitrion || 'Sin anfitrión'}
                      </Typography>
                    </Box>
                    
                    {/* Date */}
                    <Box sx={{ textAlign: 'center', minWidth: 80 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>Fecha límite</Typography>
                      <Label color={activity.is_active === false ? 'error' : 'success'} sx={{ fontSize: '0.65rem', height: 16, display: 'block', mt: 0.5 }}>
                        {dayjs(activity.lastEnrollDate).format('DD/MM/YY')}
                      </Label>
                    </Box>
                    
                    {/* Cupos */}
                    <Box sx={{ textAlign: 'center', minWidth: 60 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>Cupos</Typography>
                      <Typography variant="caption" fontWeight={500} sx={{ display: 'block', mt: 0.5 }}>
                        {activity.limitSpaces ? `${activity.participantsNumber}` : '∞'}
                      </Typography>
                    </Box>
                    
                    {/* Status */}
                    <Box sx={{ minWidth: 70 }}>
                      <Chip 
                        label={activity.is_active !== false ? 'Activa' : 'Inactiva'}
                        color={activity.is_active !== false ? 'success' : 'error'}
                        size="small"
                        sx={{ fontSize: '0.65rem', height: 20 }}
                      />
                    </Box>
                    
                    {/* Actions */}
                    <Stack direction="row" spacing={0.5} sx={{ minWidth: 120 }}>
                      <Button 
                        size="small"
                        variant="contained" 
                        onClick={() => { setOpenModalActivities(true); setValuesToEdit(activity) }}
                        sx={{ fontSize: '0.65rem', py: 0.25, px: 1, minWidth: 'auto' }}
                      >
                        Editar
                      </Button>
                      <Button 
                        size="small"
                        variant="outlined" 
                        onClick={() => navigate(`/activities/details/${activity._id}`)}
                        sx={{ fontSize: '0.65rem', py: 0.25, px: 1, minWidth: 'auto' }}
                      >
                        Ver
                      </Button>
                    </Stack>
                    
                    {/* Menu */}
                    <IconButton size="small" onClick={(e) => handleOpenMenu(e, activity)} sx={{ p: 0.5 }}>
                      <Iconify icon={'eva:more-vertical-fill'} sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Box>
                </Card>
              ))}
            </Stack>
            
            {activityList.length === 0 && !loading && (
              <Paper sx={{ textAlign: 'center', py: 6, borderRadius: 3 }}>
                <Typography variant="h5" paragraph color="text.secondary">
                  📅 Sin actividades
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  No se encontraron actividades. Intente con otra búsqueda.
                </Typography>
              </Paper>
            )}
          </Box>

          <Pagination
            count={totalPages}
            page={page}
            onChange={handleChangePage}
            sx={{ 
              display: 'flex', 
              justifyContent: isMobile ? 'center' : 'flex-end',
              p: 2 
            }}
          />
        </Card>

      <Popover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          sx: {
            p: 1,
            width: 160,
            '& .MuiMenuItem-root': {
              px: 1,
              typography: 'body2',
              borderRadius: 0.75,
            },
          },
        }}
      >
        <MenuItem onClick={() => { setOpenModalActivities(true); setOpen(null) }}>
          <Iconify icon={'eva:edit-fill'} sx={{ mr: 2 }} />
          Editar
        </MenuItem>
        <MenuItem onClick={() => {
          window.navigator.clipboard.writeText(`${process.env.REACT_APP_DOMAIN_URL}/addParticipants/${valuesToEdit._id}`);
          setOpen(null);
        }}>
          <Iconify icon={'eva:copy-fill'} sx={{ mr: 2 }} />
          Copiar enlace
        </MenuItem>
        <MenuItem onClick={() => window.open(`${process.env.REACT_APP_DOMAIN_URL}/activities/details/${valuesToEdit._id}`)}>
          <Iconify icon={'pepicons-pop:people'} sx={{ mr: 2 }} />
          Ver registros
        </MenuItem>
        <MenuItem onClick={() => window.open(`${process.env.REACT_APP_DOMAIN_URL}/activities/cert/${valuesToEdit._id}`)}>
          <Iconify icon={'fluent:certificate-24-filled'} sx={{ mr: 2 }} />
          Certificados
        </MenuItem>
        
        <MenuItem onClick={() => window.open(`${process.env.REACT_APP_DOMAIN_URL}/activities/qr-confirmation/${valuesToEdit._id}`, '_blank')}>
          <Iconify icon={'eva:camera-fill'} sx={{ mr: 2 }} />
          Confirmar QR
        </MenuItem>
      </Popover>

      <CustomModal
        open={openModalActivities}
        modalStyle={{ maxWidth: 800 }}
        title={"Formulario de Actividades"}
      >
        <FormCreateActivities
          onClose={() => setOpenModalActivities(false)}
          getValues={() => {
            getActivities();
            setOpenModalActivities(false);
            setValuesToEdit({});
            setOpen(null);
          }}
          valuesToEdit={valuesToEdit}
        />
      </CustomModal>
    </>
  );
}